import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button } from 'antd';
import { Header, Content } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSecureService } from '../../store/service';
import SidebarContent from '../Sidebar/SidebarContent';
import NotificationPermissionBanner from '../NotificationPermissionBanner/NotificationPermissionBanner';
import mainLayoutStyles from '../../layouts/mainLayout.module.scss';

interface AuthCheckerProps {
  children: React.ReactNode;
  isMobile: boolean;
  selectedKeys: string[];
  onMenuClick: ({ key }: { key: string }) => void;
  onMobileDrawerOpen: () => void;
}

const getSavedSidebarCollapsed = (): boolean => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return JSON.parse(saved);
    }
  } catch {
    // ignore
  }
  return false;
};

const AuthChecker: React.FC<AuthCheckerProps> = ({
  children,
  isMobile,
  selectedKeys,
  onMenuClick,
  onMobileDrawerOpen
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { getEntryByPath, startSessionCheck, refreshUserInfo, subscribeWebPushOnLogin } = useSecureService();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(getSavedSidebarCollapsed);
  const [authChecked, setAuthChecked] = useState(false);

  const handleSidebarToggle = () => {
    const next = !isSidebarCollapsed;
    setIsSidebarCollapsed(next);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
    document.documentElement.classList.toggle('sidebar-collapsed', next);
  };

  useEffect(() => {
    const init = async () => {
      if (window.location.pathname === '/login') {
        setIsCheckingAuth(false);
        setAuthChecked(true);
        return;
      }

      // Auth kontrolü sadece bir kez yapılsın
      if (authChecked) {
        return;
      }

      setIsCheckingAuth(true);
      
      // Path kontrolü ile FileDetail sayfasını tespit et
      const currentPath = window.location.pathname.substring(1); // Başındaki /'yi kaldır
      const isVersionsPage = currentPath.endsWith('/versions');
      const cleanPath = isVersionsPage ? currentPath.replace('/versions', '') : currentPath;
      
      let isFilePage = false;
      
      if (cleanPath && cleanPath.includes('/')) {
        try {
          const entryResponse = await getEntryByPath(cleanPath);
          if (entryResponse.success && entryResponse.data) {
            isFilePage = entryResponse.data.entryType === 'File';
          }
        } catch (error) {
          // Path servisi hata verirse varsayılan olarak FileDetail sayfası değil kabul et
          isFilePage = false;
        }
      }
      
      // FileDetail sayfası dışındaki tüm sayfalarda login kontrolü
      if (!isAuthenticated && !isFilePage) {
        window.location.href = '/login';
        return;
      }
      
      setIsCheckingAuth(false);
      setAuthChecked(true);
    };
  
    init();
  }, [isAuthenticated, authChecked, getEntryByPath]); 

  // Düzenli oturum kontrolü ve notification count güncelleme başlat
  useEffect(() => {
    if (isAuthenticated && !window.location.pathname.includes('/login')) {
      const cleanup = startSessionCheck();
      return cleanup;
    }
  }, [isAuthenticated, startSessionCheck]);

  // Sayfa değiştiğinde me servisini çağır (sadece bir kez)
  useEffect(() => {
    if (isAuthenticated && !window.location.pathname.includes('/login')) {
      const callMeService = async () => {
        try {
          await refreshUserInfo();
        } catch (error) {
          console.error('Error calling me service on page change:', error);
        }
      };
      
      callMeService();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Sadece pathname değiştiğinde çağrılır; isAuthenticated/refreshUserInfo sabit
  }, [location.pathname]);

  // Her login'de subscription API'yi çağır: backend bu tarayıcıda son giriş yapan kullanıcıyı bilsin (B'ye push gitsin, A'ya değil).
  // İzin granted ise payload gider, değilse boş body gider — popup açılmaz.
  const subscribeRef = useRef(subscribeWebPushOnLogin);
  subscribeRef.current = subscribeWebPushOnLogin;

  useEffect(() => {
    if (!isAuthenticated) return;
    if (window.location.pathname.includes('/login')) return;
    if (typeof Notification === 'undefined') return;

    const run = async () => {
      try {
        await subscribeRef.current();
      } catch {
        // sessiz
      }
    };

    const timer = setTimeout(run, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  if (isCheckingAuth) {
    return (
      <Layout className={`${mainLayoutStyles.layout} ${mainLayoutStyles.ready}`}>
        {!isMobile && (
          <Sider
            width="10%"
            collapsedWidth={68}
            collapsed={isSidebarCollapsed}
            className={mainLayoutStyles.sider}
          >
            <SidebarContent 
              selectedKeys={selectedKeys}
              onMenuClick={onMenuClick}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={handleSidebarToggle}
            />
          </Sider>
        )}
        <Layout className={`${mainLayoutStyles.mainColumn} ${isSidebarCollapsed ? mainLayoutStyles.layoutCollapsed : ''}`}>
          <Header className={mainLayoutStyles.header}>
            {isMobile && (
              <Button 
                type="primary" 
                icon={<img src="/layout.svg" alt="menu" width={20} height={20} />}
                onClick={onMobileDrawerOpen}
                className={mainLayoutStyles.mobileMenuButton}
              />
            )}
          </Header>
          <Content className={mainLayoutStyles.content} />
        </Layout>
      </Layout>
    );
  }

  return (
    <>
      {children}
      {isAuthenticated && <NotificationPermissionBanner />}
    </>
  );
};

export default AuthChecker; 
